import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/components/badges";
import { DatabaseBackup, Download, Link2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function BackupsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: backups = [] } = useQuery({
    queryKey: ["admin-backups"],
    queryFn: async () => (await api.get("/admin/backups")).data,
  });

  const backupMut = useMutation({
    mutationFn: async () => (await api.post("/admin/backups")).data,
    onSuccess: () => { toast.success(t("admin.toastBackupCreated")); qc.invalidateQueries({ queryKey: ["admin-backups"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const copyRestoreLink = async (bid) => {
    try {
      const { data } = await api.get(`/admin/backups/${bid}/link`);
      await navigator.clipboard.writeText(data.url);
      toast.success(t("admin.toastLinkCopied"));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t("admin.backupsCard")}</CardTitle>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => backupMut.mutate()} disabled={backupMut.isPending} data-testid="admin-create-backup">
          {backupMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DatabaseBackup className="mr-2 h-4 w-4" />} {t("admin.createBackup")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">{t("admin.backupsNote")}</p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>{t("admin.thCreatedAt")}</TableHead><TableHead>{t("admin.thBy")}</TableHead><TableHead>{t("common.file")}</TableHead><TableHead>{t("common.size")}</TableHead><TableHead>{t("admin.thRecords")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {backups.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500" data-testid="backups-empty">{t("admin.backupsEmpty")}</TableCell></TableRow>}
              {backups.map((b) => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{fmtDateTime(b.created_at)}</TableCell>
                  <TableCell className="text-xs">{b.created_by}</TableCell>
                  <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                  <TableCell className="font-mono text-xs">{(b.size / 1024).toFixed(0)} KB</TableCell>
                  <TableCell className="font-mono text-xs">{Object.values(b.counts || {}).reduce((s, n) => s + n, 0)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title={t("library.downloadT")} onClick={() => window.open(`/api/admin/backups/${b.id}/download`, "_blank")} data-testid={`backup-download-${b.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title={t("admin.restoreLinkT")} onClick={() => copyRestoreLink(b.id)} data-testid={`backup-restore-link-${b.id}`}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
